import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  GetApp as DownloadIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ResourceContactForm from '../../components/forms/ResourceContactForm';

interface ComplianceItem {
  id: string;
  title: string;
  description: string;
  required: boolean;
  category: string;
}

const complianceItems: ComplianceItem[] = [
  // Pillar 1: Licensing and Good Standing
  {
    id: 'license-current',
    title: 'Current Title Agent License',
    description: 'Verify all title agents have current, valid licenses in good standing',
    required: true,
    category: 'licensing',
  },
  {
    id: 'license-display',
    title: 'License Display',
    description: 'All required licenses are prominently displayed in office',
    required: true,
    category: 'licensing',
  },
  {
    id: 'continuing-education',
    title: 'Continuing Education Compliance',
    description: 'All agents have completed required continuing education credits',
    required: true,
    category: 'licensing',
  },
  
  // Pillar 2: Escrow Account Protections
  {
    id: 'segregated-accounts',
    title: 'Segregated Escrow Accounts',
    description: 'Client funds are maintained in segregated escrow accounts',
    required: true,
    category: 'escrow',
  },
  {
    id: 'monthly-reconciliation',
    title: 'Monthly Account Reconciliation',
    description: 'Escrow accounts are reconciled monthly with proper documentation',
    required: true,
    category: 'escrow',
  },
  {
    id: 'escrow-accounting',
    title: 'Proper Escrow Accounting',
    description: 'Detailed records maintained for all escrow transactions',
    required: true,
    category: 'escrow',
  },
  {
    id: 'shortage-overage-procedures',
    title: 'Shortage/Overage Procedures',
    description: 'Written procedures for handling account shortages and overages',
    required: true,
    category: 'escrow',
  },
  
  // Pillar 3: Closing Protection and Privacy
  {
    id: 'closing-protection-coverage',
    title: 'Closing Protection Coverage',
    description: 'Appropriate closing protection coverage is in place',
    required: true,
    category: 'privacy',
  },
  {
    id: 'privacy-protection',
    title: 'Privacy Protection Procedures',
    description: 'Written procedures for protecting customer private information',
    required: true,
    category: 'privacy',
  },
  {
    id: 'information-security',
    title: 'Information Security Measures',
    description: 'Adequate measures to protect against unauthorized access to information',
    required: true,
    category: 'privacy',
  },
  
  // Pillar 4: Competency and Training
  {
    id: 'employee-training',
    title: 'Employee Training Program',
    description: 'Comprehensive training program for all employees',
    required: true,
    category: 'training',
  },
  {
    id: 'compliance-training',
    title: 'Compliance Training',
    description: 'Regular training on compliance requirements and updates',
    required: true,
    category: 'training',
  },
  {
    id: 'competency-testing',
    title: 'Competency Testing',
    description: 'Regular testing to ensure employee competency',
    required: false,
    category: 'training',
  },
  
  // Pillar 5: Consumer Disclosures
  {
    id: 'disclosure-delivery',
    title: 'Timely Disclosure Delivery',
    description: 'All required disclosures are delivered in a timely manner',
    required: true,
    category: 'disclosures',
  },
  {
    id: 'disclosure-documentation',
    title: 'Disclosure Documentation',
    description: 'Proof of disclosure delivery is maintained',
    required: true,
    category: 'disclosures',
  },
  {
    id: 'disclosure-updates',
    title: 'Updated Disclosure Forms',
    description: 'All disclosure forms are current and compliant',
    required: true,
    category: 'disclosures',
  },
  
  // Pillar 6: Prompt Claim Reporting
  {
    id: 'claim-procedures',
    title: 'Claim Reporting Procedures',
    description: 'Written procedures for prompt claim reporting',
    required: true,
    category: 'claims',
  },
  {
    id: 'claim-training',
    title: 'Claim Reporting Training',
    description: 'Staff trained on claim identification and reporting',
    required: true,
    category: 'claims',
  },
  {
    id: 'claim-documentation',
    title: 'Claim Documentation',
    description: 'Proper documentation maintained for all claims',
    required: true,
    category: 'claims',
  },
];

const categoryTitles = {
  licensing: 'Pillar 1: Licensing and Good Standing',
  escrow: 'Pillar 2: Escrow Account Protections',
  privacy: 'Pillar 3: Closing Protection and Privacy',
  training: 'Pillar 4: Competency and Training',
  disclosures: 'Pillar 5: Consumer Disclosures',
  claims: 'Pillar 6: Prompt Claim Reporting',
};

const ALTAComplianceChecklist: React.FC = () => {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());

  const handleItemCheck = (itemId: string) => {
    const newCheckedItems = new Set(checkedItems);
    if (newCheckedItems.has(itemId)) {
      newCheckedItems.delete(itemId);
    } else {
      newCheckedItems.add(itemId);
    }
    setCheckedItems(newCheckedItems);
  };

  const getComplianceScore = () => {
    const requiredItems = complianceItems.filter(item => item.required);
    const checkedRequiredItems = requiredItems.filter(item => checkedItems.has(item.id));
    return {
      current: checkedRequiredItems.length,
      total: requiredItems.length,
      percentage: Math.round((checkedRequiredItems.length / requiredItems.length) * 100),
    };
  };

  const getComplianceStatus = () => {
    const score = getComplianceScore();
    if (score.percentage === 100) return { status: 'Excellent', color: '#16a34a' };
    if (score.percentage >= 80) return { status: 'Good', color: '#f59e0b' };
    if (score.percentage >= 60) return { status: 'Fair', color: '#f97316' };
    return { status: 'Needs Improvement', color: '#dc2626' };
  };

  const groupedItems = complianceItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, ComplianceItem[]>);

  const complianceScore = getComplianceScore();
  const complianceStatus = getComplianceStatus();

  return (
    <>
      <Helmet>
        <title>ALTA Best Practices Compliance Checklist | Professional Resources | MAD Title Agency</title>
        <meta
          name="description"
          content="Comprehensive ALTA Best Practices compliance checklist for title professionals. Ensure full compliance with all six pillars of ALTA requirements."
        />
        <meta
          name="keywords"
          content="ALTA Best Practices, title compliance, title professional checklist, ALTA requirements, title insurance compliance"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            ALTA Compliance Checklist
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Professional Resource"
                sx={{
                  backgroundColor: '#059669',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                ALTA Best Practices Compliance Checklist
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Comprehensive checklist to ensure compliance with all six pillars of ALTA Best Practices
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: '#f59e0b',
                  '&:hover': { backgroundColor: '#d97706' },
                }}
              >
                Download PDF Checklist
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <SecurityIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  6 Compliance Pillars
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Complete Coverage
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Compliance Score Card */}
        <Card sx={{ mb: 6, p: 4 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: '#1e3a8a' }}>
                Your Compliance Score
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h2" sx={{ fontWeight: 700, color: complianceStatus.color, mr: 2 }}>
                  {complianceScore.percentage}%
                </Typography>
                <Chip
                  label={complianceStatus.status}
                  sx={{
                    backgroundColor: complianceStatus.color,
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {complianceScore.current} of {complianceScore.total} required items completed
              </Typography>
              <LinearProgress
                variant="determinate"
                value={complianceScore.percentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: complianceStatus.color,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: '#f8fafc' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Quick Actions
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <AssignmentIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Review unchecked items" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <DownloadIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Download full report" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <SecurityIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Schedule compliance review" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Card>

        {/* Introduction */}
        <Alert severity="info" sx={{ mb: 6 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            About ALTA Best Practices
          </Typography>
          The American Land Title Association (ALTA) Best Practices provide guidance for title insurance agents 
          and abstracters on how to achieve and maintain professional standards. This interactive checklist 
          covers all six pillars to help ensure your operation meets these important industry standards.
        </Alert>

        {/* Compliance Checklist by Category */}
        <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
          Compliance Checklist
        </Typography>

        {Object.entries(groupedItems).map(([category, items]) => (
          <Accordion key={category} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                  {categoryTitles[category as keyof typeof categoryTitles]}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  {items.filter(item => item.required && checkedItems.has(item.id)).length === 
                   items.filter(item => item.required).length ? (
                    <CheckIcon sx={{ color: '#16a34a', mr: 1 }} />
                  ) : (
                    <WarningIcon sx={{ color: '#f59e0b', mr: 1 }} />
                  )}
                  <Typography variant="body2">
                    {items.filter(item => item.required && checkedItems.has(item.id)).length}/
                    {items.filter(item => item.required).length} Required
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {items.map((item) => (
                  <Grid item xs={12} key={item.id}>
                    <Card
                      sx={{
                        p: 2,
                        border: checkedItems.has(item.id) ? '2px solid #16a34a' : '1px solid #e2e8f0',
                        backgroundColor: checkedItems.has(item.id) ? '#f0fdf4' : 'white',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checkedItems.has(item.id)}
                              onChange={() => handleItemCheck(item.id)}
                              color="primary"
                            />
                          }
                          label=""
                          sx={{ mt: 0 }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                              {item.title}
                            </Typography>
                            {item.required && (
                              <Chip
                                label="Required"
                                size="small"
                                color="error"
                                sx={{ ml: 2 }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Additional Resources */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Additional Compliance Resources
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <SecurityIcon sx={{ fontSize: 48, color: '#1e3a8a', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  ALTA Best Practices Manual
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Complete guide to implementing and maintaining ALTA Best Practices in your operation.
                </Typography>
                <Button variant="outlined" fullWidth>
                  Download Manual
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <AssignmentIcon sx={{ fontSize: 48, color: '#059669', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Compliance Training
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Professional training courses to ensure your team understands all compliance requirements.
                </Typography>
                <Button variant="outlined" fullWidth>
                  View Courses
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <CheckIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Compliance Audit Service
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Professional audit services to ensure your operation meets all regulatory requirements.
                </Typography>
                <Button variant="outlined" fullWidth>
                  Request Audit
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Form Section */}
        <ResourceContactForm
          resourceTitle="ALTA Compliance Checklist"
          resourceType="professional"
          formType="consultation"
        />
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
              Need Compliance Support?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Our compliance experts can help ensure your operation meets all ALTA Best Practices requirements.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  backgroundColor: '#f59e0b',
                  '&:hover': { backgroundColor: '#d97706' },
                  px: 4,
                  py: 1.5,
                }}
              >
                Compliance Consultation
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/resources"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  '&:hover': { borderColor: '#f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.1)' },
                }}
              >
                More Professional Resources
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ALTAComplianceChecklist; 